// src/app/practice/practice-filter.ts
//
// Pure filter + sort logic for the practice topic grid. Extracted out
// of PracticeClient so it can be unit-tested without React. The grid
// has bitten us before with subtle off-by-one sort behavior (untaken
// vs. zero-correct topics get different sort keys), so pinning the
// rules here keeps a future "tweak the sort" change from regressing
// the others.

import type { TopicPerformance } from './page'

export type SectionFilter = 'ALL' | 'RW' | 'MATH'
export type SortMode = 'weakest' | 'strongest' | 'az'

/**
 * Build a stable, UNIQUE React key for a topic card.
 *
 * Why this exists: the seeded SAT data has three Math rows that share
 * `topic = "Math"` and `section = "MATH"` but differ on `domain`
 * ("Advanced Math" / "Algebra" / "Problem Solving and Data Analysis").
 * Keying just on `topic + section` collapses all three into the same
 * React key — React then warns about duplicates and reconciliation
 * goes off the rails. Most visibly: filtering ALL → RW leaves the
 * orphaned MATH cards in the DOM because React can't identify which
 * "Math-MATH" element to remove.
 *
 * Including `domain` makes the key unique. Falling back to "" keeps
 * the key well-formed for older rows where domain is null.
 */
export function topicCardKey(t: TopicPerformance): string {
  return `${t.section}::${t.topic}::${t.domain ?? ''}`
}

/**
 * Filter the topic list to one section, then sort by the chosen mode.
 *
 * Sort rules:
 *   - "az": alphabetical by topic name
 *   - "weakest" / "strongest": attempted topics by accuracy %; NEVER-attempted
 *     topics (`total === 0`) are neither weak nor strong, so they always sort
 *     to the END (alphabetical among themselves), in BOTH modes.
 *
 * This mirrors the dashboard Mastery Map's ordering EXACTLY (see MasteryMap.tsx)
 * so the two topic lists read identically — previously this grid floated
 * untaken topics to the TOP of "weakest", which made the same set of topics
 * appear in a different order than the map (Visakh: "make the two the same").
 *
 * Returns a NEW array — the input is never mutated.
 */
export function filterAndSortTopics(
  topics: readonly TopicPerformance[],
  sectionFilter: SectionFilter,
  sortMode: SortMode,
): TopicPerformance[] {
  // Filter — early-out for ALL avoids an unnecessary array copy.
  const filtered =
    sectionFilter === 'ALL' ? [...topics] : topics.filter((t) => t.section === sectionFilter)

  // Sort in place on the copy.
  filtered.sort((a, b) => {
    if (sortMode === 'az') return a.topic.localeCompare(b.topic)
    const aUntouched = a.total === 0
    const bUntouched = b.total === 0
    if (aUntouched !== bUntouched) return aUntouched ? 1 : -1 // untaken last
    if (aUntouched) return a.topic.localeCompare(b.topic)
    const pctA = a.correct / a.total
    const pctB = b.correct / b.total
    return sortMode === 'weakest' ? pctA - pctB : pctB - pctA
  })

  return filtered
}

export type TopicSectionGroup = {
  key: 'RW' | 'MATH'
  label: string
  items: TopicPerformance[]
}

/**
 * Split an already-filtered + sorted topic list into Reading & Writing / Math
 * groups so the grid can render them as two visually distinct sections instead
 * of one intermixed wall of cards.
 *
 * Order within each group is preserved (Array.filter is stable, so the caller's
 * chosen sort carries through), and empty groups are dropped. RW is listed
 * before Math to match the test's section order. When the caller has already
 * narrowed to one section, this simply returns that single group.
 */
export function groupBySection(topics: readonly TopicPerformance[]): TopicSectionGroup[] {
  const groups: TopicSectionGroup[] = []
  const rw = topics.filter((t) => t.section === 'RW')
  const math = topics.filter((t) => t.section === 'MATH')
  if (rw.length > 0) groups.push({ key: 'RW', label: 'Reading & Writing', items: rw })
  if (math.length > 0) groups.push({ key: 'MATH', label: 'Math', items: math })
  return groups
}

/**
 * The difficulty a practice session should run at.
 *
 * 'ADAPTIVE' is the engine choosing the level from the student's answers as
 * they go; E/M/H pin one tagged level for the whole session. Kept in the same
 * vocabulary the session page already uses, so the value passes straight
 * through the URL without translation.
 */
export type PracticeDifficulty = 'ADAPTIVE' | 'E' | 'M' | 'H'

/**
 * Where the "Practice" button on a topic card goes.
 *
 * WHY THIS IS A FUNCTION AND NOT A TEMPLATE AT THE CALL SITE. The session page
 * has always read `?difficulty=` and defaulted to ADAPTIVE when it is absent —
 * but nothing in the product ever set it. Every session therefore started
 * adaptive no matter what the student chose, while the Practice page's own copy
 * told them to choose a difficulty. The parameter was live at one end and dead
 * at the other, which is exactly the kind of gap a link built inline hides.
 *
 * ADAPTIVE is omitted rather than spelled out: it is the session's default, so
 * the shorter URL and the longer one mean the same thing, and the absence says
 * "nothing pinned" more plainly than the word does.
 */
export function practiceSessionHref(topic: string, difficulty: PracticeDifficulty): string {
  const base = `/practice/session?topic=${encodeURIComponent(topic)}&count=all`
  return difficulty === 'ADAPTIVE' ? base : `${base}&difficulty=${difficulty}`
}
